// auth-context.tsx - React Router Version
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ApiClient } from "@client/lib/api-client";

interface User {
  id: number;
  username: string;
  displayName: string | null;
  email: string | null;
  isActive: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string; accessCode?: string }>;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const clearAuthAndRedirect = () => {
    setUser(null);
    if (location.pathname !== '/login') {
      navigate('/login', { replace: true });
    }
  };

  const checkAuth = async (): Promise<void> => {
    try {
      console.log('[Auth] Checking authentication status...');
      
      const response = await ApiClient.get<{ 
        success: boolean; 
        isAuthenticated: boolean; 
        user?: User;
        authMethod?: string;
      }>('/auth/status');

      console.log('[Auth] Server response:', {
        success: response.success,
        isAuthenticated: response.isAuthenticated,
        authMethod: response.authMethod,
        hasUser: !!response.user
      });

      if (response.success && response.isAuthenticated && response.user) {
        console.log('[Auth] ✅ User authenticated:', response.user.username);
        setUser(response.user);
      } else {
        console.log('[Auth] ❌ Not authenticated');
        clearAuthAndRedirect();
      }
    } catch (error) {
      console.error('[Auth] Check failed:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('401') || error.message.includes('403')) {
          clearAuthAndRedirect();
        }
      } else {
        clearAuthAndRedirect();
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (username: string, password: string): Promise<{ success: boolean; message?: string; accessCode?: string }> => {
    setIsLoading(true);

    try {
      console.log('[Auth] Attempting login:', username);
      
      const response = await ApiClient.post<{
        success: boolean;
        message?: string;
        user?: User;
        accessCode?: string;
      }>('/auth/login', { username, password });

      if (response.success && response.user) {
        console.log('[Auth] ✅ Login successful');
        setUser(response.user);
        
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 100);

        return {
          success: true,
          accessCode: response.accessCode,
          message: response.message || 'Login successful'
        };
      } else {
        return {
          success: false,
          message: response.message || "Login failed"
        };
      }
    } catch (error) {
      console.error('[Auth] Login error:', error);

      if (error instanceof Error) {
        if (error.message.includes('401')) {
          return { success: false, message: "Invalid credentials" };
        } else if (error.message.includes('400')) {
          return { success: false, message: "Invalid input" };
        }
      }

      return { success: false, message: "Connection error" };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      console.log('[Auth] Logging out...');
      await ApiClient.post('/auth/logout');
      console.log('[Auth] ✅ Server logout successful');
    } catch (error) {
      console.error('[Auth] Server logout failed:', error);
    } finally {
      setUser(null);
      navigate('/login', { replace: true });
      console.log('[Auth] ✅ Local logout completed');
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const ProtectedRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      console.log('[Auth] Redirecting to login');
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse">
            <p className="text-slate-600">Redirecting to login...</p>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};